import { AdditiveBlending, Color, InstancedMesh, Matrix4, MeshBasicNodeMaterial, PlaneGeometry, Quaternion, Vector3 } from 'three/webgpu';
import { abs, float, mx_noise_float, smoothstep, time, uv, vec3, vec4, max } from 'three/tsl';

const tmpMat = new Matrix4();
const tmpPos = new Vector3();
const tmpQuat = new Quaternion();
const tmpScale = new Vector3();
const Z_AXIS = new Vector3(0, 0, 1);
const BEAM_WIDTH = 0.55;

/** Lightning segments: stretched additive quads with a noise-jittered core. */
export class BeamPool {
  readonly mesh: InstancedMesh;
  private count = 0;
  private readonly capacity: number;

  constructor(capacity: number, tint: number) {
    this.capacity = capacity;
    const geometry = new PlaneGeometry(1, 1);
    const material = new MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    material.depthTest = false;
    material.blending = AdditiveBlending;

    const p = uv().sub(0.5).mul(2);
    const wobble = mx_noise_float(vec3(uv().x.mul(9), time.mul(22), float(0.5))).mul(0.55);
    const yy = abs(p.y.add(wobble));
    const core = smoothstep(0.35, 0.02, yy);
    const glow = smoothstep(1.0, 0.1, yy).mul(0.35);
    const endFade = smoothstep(1.0, 0.85, abs(p.x));
    const c = new Color(tint);
    material.colorNode = vec4(c.r, c.g, c.b, 1);
    material.opacityNode = max(core, glow).mul(endFade);

    this.mesh = new InstancedMesh(geometry, material, capacity);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 8;
    this.mesh.count = 0;
  }

  begin(): void {
    this.count = 0;
  }

  push(x1: number, y1: number, x2: number, y2: number, thickness: number): void {
    if (this.count >= this.capacity) return;
    const i = this.count++;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 0.001;
    tmpPos.set((x1 + x2) / 2, (y1 + y2) / 2, 0.75);
    tmpQuat.setFromAxisAngle(Z_AXIS, Math.atan2(dy, dx));
    tmpScale.set(len + 0.2, BEAM_WIDTH * thickness, 1);
    tmpMat.compose(tmpPos, tmpQuat, tmpScale);
    this.mesh.setMatrixAt(i, tmpMat);
  }

  end(): void {
    this.mesh.count = this.count;
    if (this.count > 0) this.mesh.instanceMatrix.needsUpdate = true;
  }
}
